import { useCallback } from "react";
import { Formik, FormikProps } from "formik";
import { Row, Col } from "antd";
import { Form, SubmitButton, Input } from "formik-antd";

import { settingService } from "/app/src/services";
import { useTranslation } from "react-i18next";
import { App as AppType, Setting as SettingType } from "/app/src/models";
import { buildParams } from "/app/src/helpers/params";
import { simpleSchemaBuilder } from "/app/src/helpers";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { handlePromiseError } from "/app/src/helpers/api";
import { Button } from "@heroui/react";

interface FormValues {
  hue_app_id: string | undefined;
  hue_client_id: string | undefined;
  hue_client_secret: string | undefined;
}

/**
 * Renders a Hue configuration component.
 *
 * @param {Object} param0 - The component props.
 * @param {AppType} param0.app - The application configuration.
 * @returns {JSX.Element} The rendered Hue component.
 */
export default function Hue({ app }: { app: AppType }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: clientId } = useQuery({
    queryKey: ["hueQuery", app.id],
    queryFn: () => {
      return settingService
        .getAll(buildParams({ number: 1, appId: app.id }))
        .then((response) => {
          if (response.settings.length > 0) {
            return response.settings[0];
          }
          return { value: "" };
        });
    },
    initialData: {},
    select: (data: SettingType) => {
      return data;
    },
  });

  const { data: clientSecret } = useQuery({
    queryKey: ["clientSecret", app.id],
    queryFn: () => {
      return settingService
        .getAll(buildParams({ number: 2, appId: app.id }))
        .then((response) => {
          if (response.settings.length > 0) {
            return response.settings[0];
          }
          return { value: "" };
        });
    },
    initialData: {},
    select: (data: SettingType) => {
      return data;
    },
  });

  const { data: hueAppId } = useQuery({
    queryKey: ["hueAppId", app.id],
    queryFn: () => {
      return settingService
        .getAll(buildParams({ number: 3, appId: app.id }))
        .then((response) => {
          if (response.settings.length > 0) {
            return response.settings[0];
          }
          return { value: "" };
        });
    },
    initialData: {},
    select: (data: SettingType) => {
      return data;
    },
  });

  const { mutateAsync: createSetting } = useMutation({
    mutationFn: (values: SettingType) => {
      return settingService.createSingle(values).then(handlePromiseError);
    },
  });
  const { mutateAsync: updateSetting } = useMutation({
    mutationFn: (values: SettingType) => {
      return settingService
        .updateSingle(values.id, values)
        .then(handlePromiseError);
    },
  });

  const invalidateQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["hueQuery", app.id] });
    queryClient.invalidateQueries({ queryKey: ["clientSecret", app.id] });
    queryClient.invalidateQueries({ queryKey: ["hueAppId", app.id] });
  }, [app.id, queryClient]);

  const clearSettings = useCallback(async () => {
    if (clientId?.id) {
      await settingService.deleteSingle(clientId.id);
    }
    if (clientSecret?.id) {
      await settingService.deleteSingle(clientSecret.id);
    }
    if (hueAppId?.id) {
      await settingService.deleteSingle(hueAppId.id);
    }
    invalidateQueries();
  }, [clientId.id, clientSecret.id, hueAppId.id, invalidateQueries]);
  const handleSubmit = useCallback(
    async (values: FormValues, actions) => {
      const hue_client_id = {
        number: 1,
        name: "client_id",
        appId: app.id,
        value: values.hue_client_id,
        type: "password",
      };
      const hue_client_secret = {
        number: 2,
        name: "client_secret",
        appId: app.id,
        value: values.hue_client_secret,
        type: "password",
      };
      const hue_app_id = {
        number: 3,
        name: "app_id",
        appId: app.id,
        value: values.hue_app_id,
      };
      if (hueAppId?.id) {
        if (values?.hue_app_id !== hueAppId.value) {
          await updateSetting({
            id: hueAppId.id,
            ...hue_app_id,
          });
        }
      } else {
        await createSetting(hue_app_id);
      }
      if (values?.hue_client_id !== "") {
        if (clientId?.id) {
          await updateSetting({
            id: clientId.id,
            ...hue_client_id,
          });
        } else {
          await createSetting(hue_client_id);
        }
      }
      if (values?.hue_client_secret !== "") {
        if (clientSecret?.id) {
          await updateSetting({
            id: clientSecret.id,
            ...hue_client_secret,
          });
        } else {
          await createSetting(hue_client_secret);
        }
      }
      invalidateQueries();
      actions.resetForm();
    },
    [
      app.id,
      clientId.id,
      clientSecret.id,
      createSetting,
      hueAppId.id,
      hueAppId.value,
      invalidateQueries,
      updateSetting,
    ],
  );
  const onFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    e.target.placeholder = "";
  }, []);

  const onBlur = useCallback(
    (field: "hue_client_id" | "hue_client_secret") =>
      (e: React.FocusEvent<HTMLInputElement>) => {
        let setting;
        if (field === "hue_client_id") setting = clientId;
        else if (field === "hue_client_secret") setting = clientSecret;
        if (setting?.id) {
          e.target.placeholder = "***********";
        }
      },
    [clientId, clientSecret],
  );

  const hueSettingsForm: (props: FormikProps<FormValues>) => JSX.Element =
    useCallback(
      ({ dirty }) => (
        <Form layout="vertical">
          <h3>
            {app.name} - {t("translation:authentication")}
          </h3>
          <Row justify="start" gutter={16}>
            <Col span={12}>
              <Form.Item
                name="hue_client_id"
                label={t("translation:client_id")}
              >
                <Input.Password
                  name="hue_client_id"
                  className={clientId?.id ? "password" : ""}
                  size="large"
                  onFocus={onFocus}
                  onBlur={onBlur("hue_client_id")}
                  placeholder={clientId?.id ? "***********" : ""}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="hue_client_secret"
                label={t("translation:client_secret")}
              >
                <Input.Password
                  name="hue_client_secret"
                  className={clientSecret?.id ? "password" : ""}
                  size="large"
                  onFocus={onFocus}
                  onBlur={onBlur("hue_client_secret")}
                  placeholder={clientSecret?.id ? "***********" : ""}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row justify="start" gutter={16}>
            <Col span={12}>
              <Form.Item name="hue_app_id" label={t("translation:app_id")}>
                <Input name="hue_app_id" className="user" size="large" />
              </Form.Item>
            </Col>
          </Row>
          <Row justify="start" gutter={16}>
            <Col span={20}>
              <SubmitButton type="primary" size="large" block disabled={!dirty}>
                {t("translation:save")}
              </SubmitButton>
            </Col>
            <Col span={4}>
              <Button
                size="md"
                radius="none"
                variant="bordered"
                color="default"
                onPress={clearSettings}
                className="border-error-default bg-white text-error-default"
                isDisabled={!clientId?.id && !clientSecret?.id && !hueAppId?.id}
              >
                {t("translation:clear_settings")}
              </Button>
            </Col>
          </Row>
        </Form>
      ),
      [
        app.name,
        clearSettings,
        clientId?.id,
        clientSecret?.id,
        hueAppId?.id,
        onBlur,
        onFocus,
        t,
      ],
    );
  return (
    <div className="appSettings">
      <Formik
        component={hueSettingsForm}
        initialValues={{
          hue_client_id: "",
          hue_client_secret: "",
          hue_app_id: hueAppId.value,
        }}
        enableReinitialize
        validationSchema={simpleSchemaBuilder([
          {
            name: "hue_client_id",
            type: "string",
            required: clientId?.id ? false : true,
          },
          {
            name: "hue_client_secret",
            type: "string",
            required: clientSecret?.id ? false : true,
          },
          { name: "hue_app_id", type: "string", required: true },
        ])}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
